import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { dashboardQuery, reportQuery } from "@/api/queries";
import { BrandLockup } from "@/components/BrandMark";
import { Markdown } from "@/components/chat/Markdown";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { ReadOnlyTiles } from "@/components/reports/ReadOnlyTiles";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Chip } from "@/components/ui/Chip";
import { Spinner } from "@/components/ui/Spinner";
import { AlertIcon, LinkIcon, SparkIcon } from "@/components/icons";
import { useRelativeTime } from "@/lib/time";

/**
 * The shared view (ACC-1 — the Viewer tier).
 *
 * **A share is a capability to read, never a query path.** This page asks for
 * the subject with a GET and nothing else: no refresh-on-open (the owner's
 * report page does one; this one must not), no refetch against a refresh
 * endpoint, no SSE, no mutations anywhere — so the mutation-error conventions
 * have nothing to apply to, and that is the point. The server enforces it as
 * well; a share session that tried would get a 403, but the page never tries.
 *
 * Outside `AppShell` on purpose: no sidebar, no composer, no toaster. Brand
 * comes from the theme like every other surface, never a literal.
 */

function isInactive(error: unknown) {
  return error instanceof ApiError && (error.code === "share_inactive" || error.status === 401);
}

function SharedFrame({
  title,
  subtitle,
  stamp,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  stamp?: string | null;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const relative = useRelativeTime(stamp);
  return (
    <div className="bg-bg text-ink min-h-dvh">
      <header className="border-border bg-surface border-b">
        <div className="mx-auto flex h-16 max-w-[100rem] items-center gap-3 px-4 sm:px-6">
          <BrandLockup size={28} />
          <span className="flex-1" />
          <ThemeToggle />
        </div>
      </header>
      <main className="mx-auto flex max-w-[100rem] flex-col gap-5 px-4 py-6 sm:px-6">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-56 flex-1">
            <h1 className="type-h2 text-ink">{title}</h1>
            {subtitle ? (
              <p className="type-support text-ink-secondary mt-1">{subtitle}</p>
            ) : null}
            <p className="type-caption text-ink-disabled mt-2 flex items-center gap-1.5">
              <LinkIcon className="size-3" />
              Shared view
              {relative ? (
                <span title={stamp ? new Date(stamp).toLocaleString() : undefined}>
                  · refreshed {relative}
                </span>
              ) : null}
            </p>
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
}

function Loading() {
  return (
    <div className="bg-bg flex min-h-dvh items-center justify-center">
      <Spinner />
    </div>
  );
}

export function SharedReportPage({ reportId }: { reportId: string }) {
  // No retry: a revoked share answers 401 and should say so at once.
  const { data: report, error, isLoading } = useQuery({ ...reportQuery(reportId), retry: false });
  if (isLoading) return <Loading />;
  if (!report) return <SharedInactivePage reason={isInactive(error) ? undefined : "missing"} />;
  return (
    <SharedFrame
      title={report.title || "Untitled report"}
      subtitle={report.question ? `“${report.question}”` : undefined}
      stamp={report.last_refreshed_at}
      actions={<ExportMenu base={`/api/reports/${report.id}`} html />}
    >
      {report.summary ? (
        <div className="type-body text-ink max-w-3xl">
          <Markdown>{report.summary}</Markdown>
        </div>
      ) : null}
      {report.insights.length ? (
        <ul className="flex max-w-3xl flex-col gap-1.5">
          {report.insights.map((insight) => (
            <li key={insight} className="type-support text-ink-secondary flex gap-2">
              <SparkIcon className="text-accent mt-0.5 size-3.5 shrink-0" />
              <span>{insight}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {report.warnings.map((warning) => (
        <p
          key={warning}
          className="bg-warning-subtle text-warning type-support rounded-element flex max-w-3xl items-start gap-2 px-3 py-2"
        >
          <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
          <span>{warning}</span>
        </p>
      ))}
      <ReadOnlyTiles tiles={report.tiles} links={report.links ?? []} />
    </SharedFrame>
  );
}

export function SharedDashboardPage({ dashboardId }: { dashboardId: string }) {
  const { data: dashboard, error, isLoading } = useQuery({
    ...dashboardQuery(dashboardId),
    retry: false,
  });
  if (isLoading) return <Loading />;
  if (!dashboard) {
    return <SharedInactivePage reason={isInactive(error) ? undefined : "missing"} />;
  }
  return (
    <SharedFrame
      title={dashboard.name || "Untitled dashboard"}
      subtitle={dashboard.description || undefined}
      stamp={dashboard.last_refreshed_at}
      actions={<ExportMenu base={`/api/dashboards/${dashboard.id}`} html />}
    >
      <ReadOnlyTiles tiles={dashboard.tiles} links={dashboard.links ?? []} />
    </SharedFrame>
  );
}

/**
 * Where every link that no longer works lands — revoked, expired, unknown,
 * or the subject deleted. One page for all of them, because telling a link
 * holder *which* would tell them which links exist. Branded, and never the
 * unlock form: somebody sent a link is not somebody who has a key.
 */
export function SharedInactivePage({ reason }: { reason?: string }) {
  const busy = reason === "rate_limited";
  return (
    <div className="bg-bg text-ink flex min-h-dvh flex-col">
      <header className="border-border bg-surface border-b">
        <div className="mx-auto flex h-16 max-w-[100rem] items-center gap-3 px-4 sm:px-6">
          <BrandLockup size={28} />
          <span className="flex-1" />
          <ThemeToggle />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="flex max-w-md flex-col items-center gap-3 text-center">
          <Chip tone={busy ? "warning" : "neutral"} icon={<LinkIcon className="size-3" />}>
            {busy ? "Too many attempts" : "Link not active"}
          </Chip>
          <h1 className="type-h2 text-ink">
            {busy ? "Try this link again shortly" : "This link is no longer active"}
          </h1>
          <p className="type-body text-ink-secondary">
            {busy
              ? "Too many links were tried from this connection in a short time."
              : "It may have been revoked or have expired. Ask whoever sent it for a new link."}
          </p>
        </div>
      </main>
    </div>
  );
}
